'use client';

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { filterCities, findCityByName } from './calculator-config';

/**
 * Выбор города с поиском. Замена нативному <datalist>: тот фильтрует варианты
 * по текущему значению, и при выбранном городе список показывал только его.
 * Здесь при открытии виден весь список, фильтрация — только после ввода.
 */
export default function CityCombobox({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (cityName: string) => void;
}) {
  const listId = useId();
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const options = useMemo(() => filterCities(query, value), [query, value]);

  useEffect(() => {
    if (!open) setQuery(value);
  }, [value, open]);

  // При открытии подсвечиваем выбранный город и прокручиваем к нему,
  // при вводе — первый найденный вариант.
  useEffect(() => {
    if (!open) return;
    const selectedIndex = options.findIndex((c) => c.name === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : options.length ? 0 : -1);
  }, [open, options, value]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const commit = (cityName: string) => {
    onChange(cityName);
    setQuery(cityName);
    setOpen(false);
  };

  const close = () => {
    const match = findCityByName(query);
    if (match) commit(match.name);
    else {
      setQuery(value);
      setOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => Math.min(Math.max(i + step, 0), options.length - 1));
    } else if (e.key === 'Enter') {
      if (open && options[activeIndex]) {
        e.preventDefault();
        commit(options[activeIndex].name);
      }
    } else if (e.key === 'Escape') {
      setQuery(value);
      setOpen(false);
    }
  };

  const activeOptionId = open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined;

  return (
    <div className="relative mt-1.5">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        autoComplete="off"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={(e) => {
          e.target.select();
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        onBlur={close}
        onKeyDown={handleKeyDown}
        className="flex h-11 w-full rounded-xl border border-slate-200 bg-white pl-3 pr-10 text-base text-slate-900 shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-950 md:text-sm"
        placeholder="Начните вводить город"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={open ? 'Скрыть список городов' : 'Показать все города'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (open) {
            close();
          } else {
            setQuery(value);
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-slate-600"
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 text-sm shadow-lg"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2 text-slate-500">Город не найден</li>
          ) : (
            options.map((city, index) => {
              const selected = city.name === value;
              return (
                <li
                  key={city.name}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={selected}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => commit(city.name)}
                  className={`flex cursor-pointer items-center justify-between px-3 py-2 ${
                    index === activeIndex ? 'bg-slate-100' : ''
                  } ${selected ? 'font-semibold text-[#214688]' : 'text-slate-700'}`}
                >
                  {city.name}
                  {selected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
